/**
 * The geometry of the flat art: what Dev/Bake renders, and what the landing
 * lays out. Both read it from here, because the two have to agree exactly. An
 * image drawn for one span and placed at another is a sky that slides against
 * the page, which is the one fault nobody would be able to name on sight.
 *
 * Every number is in viewport-heights, which is the unit the shader works in
 * on both axes: `px` there is `(uv.x - 0.5) * aspect`, so one unit across is
 * the same distance as one unit down. That is what lets the page lay a picture
 * out as `width: 220vh` and have it land pixel-correct at any window shape,
 * cropping at the sides rather than stretching.
 */
export const BAKE = {
  /** Pixels to a viewport-height: file size against how crisp an edge stays. */
  vhPx: 640,
  /**
   * Widest frame the art fills. Narrower windows crop into it, which is exact;
   * anything wider than this would run out of picture at the sides.
   */
  aspect: 2.2,
} as const;

/** How fast the cloud travels on screen, as a fraction of scroll: the shader's cloudY. */
export const CLOUD_RATE = 0.62;

/** A smoothstep matching the shader's, so the fades cut over at the same place. */
export function ramp(edge0: number, edge1: number, x: number): number {
  const t = Math.min(Math.max((x - edge0) / (edge1 - edge0), 0), 1);
  return t * t * (3 - 2 * t);
}

/**
 * The places the sky shows through. The rest of the page is covered, by rock
 * or by a band with its own colour, so the sky is drawn in these alone: live,
 * one scissored rectangle each and nothing at all while none is on screen
 * (LandingSky), or as a still per window (SkyWindow). It used to be one sky
 * the whole page long, and more than half of that was only ever painted
 * behind a band.
 *
 * Each window runs the same composition from its own top edge (a light near
 * the top, cloud packed into the corners, parallax against the window at
 * CLOUD_RATE), and `seed` shifts the noise so no two show the same cloud. The
 * hero's is zero: its sky is the one the logo was placed in.
 *
 * `below` and `above` stretch a window past its section, in viewport-heights.
 * The hero's reaches into the route, whose rock has sky between its back
 * ridges down to the far slab (tFar in the shader, 0.38). The gallery's
 * reaches up over the last of the route, where the near slab stops just short
 * of the section's bottom edge. `reach` is the tallest the window is expected
 * to be, which sizes its still: a gallery on a narrow tablet runs to three
 * and a bit screens, and past the still the window is the void, which is what
 * the cloud has closed into by then anyway.
 */
export const WINDOWS = {
  hero: { seed: [0, 0], below: 0.4, above: 0, reach: 1.4 },
  made: { seed: [17.3, -6.1], below: 0, above: 0.03, reach: 3.5 },
  closing: { seed: [-11.8, 23.6], below: 0, above: 0, reach: 1.3 },
} as const;

export type SkyWindowName = keyof typeof WINDOWS;

/**
 * Viewport-heights a window's still spans. A row `w` down the window is on
 * screen while the window has scrolled between `w - 1` and `w`, and the still
 * slides down it at `1 - CLOUD_RATE` of that, so the rows it ever shows run
 * from its top to `CLOUD_RATE * reach + (1 - CLOUD_RATE)`.
 */
export const windowSpan = (reach: number): number => CLOUD_RATE * reach + (1 - CLOUD_RATE);

/**
 * The scroll a still is rendered at, so its top row is the top of the frame
 * at the window's own scroll zero: the canvas is centred on the frame, and
 * the lane the shader samples at is `vy - CLOUD_RATE * scroll`.
 */
export const windowScroll = (span: number): number => (span / 2 - 0.5) / CLOUD_RATE;

/**
 * Each rock formation. Only the ridges are a picture: below the nearest
 * stratum's slab every one of these is a single flat tone, because the near
 * body covers the others and has nothing drawn on it. So the art stops at
 * `floor` and a plain box carries on from there.
 *
 * That split is not only thrift. It is what lets the route's map rings lie on
 * the cavern floor and pass behind the crags, which is what a map drawn on a
 * floor does; one picture of the whole formation can only be in front of them
 * or behind them, and both look wrong.
 *
 * `margin` is the headroom the ridges need above the anchor and `floor` is how
 * far below it the flat tone starts. `run` is where the near slab's own bottom
 * lies: the route's formation ends there, on an underside as crumbled as its
 * top, so that edge is a third picture (`hem`) hung off the section's bottom.
 * The footer's runs off the end of the document and never has one.
 */
export const ROCK = {
  route: {
    run: 2.6,
    margin: 0.7,
    floor: 0.72,
    /* How far above the section's bottom edge the hem starts, and how tall it
       is. The slab ends 0.02 below that edge and its boulders hang about 0.2
       further, so the picture has to reach past both and overlap the flat tone
       above by enough that the join cannot be found. */
    hem: { above: 0.18, span: 0.5 },
  },
  footer: { run: 2.4, margin: 0.5, floor: 0.3, hem: null },
} as const;

/** Viewport-heights a rock image spans: its headroom plus its ridges. */
export const rockSpan = (rock: Rock): number => rock.margin + rock.floor;

/** Where the shader's anchor for a formation lands on its own image. */
export const rockAnchor = (rock: Rock): number => (rock.floor - rock.margin) / 2;

/** Where the shader's anchor lands on a hem image, measured the same way. */
export const hemAnchor = (rock: Rock): number =>
  rock.hem ? rock.hem.span / 2 + rock.run - rock.hem.above : 0;

export type Hem = { above: number; span: number };
export type Rock = { run: number; margin: number; floor: number; hem: Hem | null };
